/**
 * Currency formatting helpers.
 *
 * The shop's curr_sym is used directly as the symbol prefix.
 * For currencies like VND that have no decimal places we detect by symbol.
 */

const NO_DECIMAL_SYMBOLS = new Set(['₫', '₩', '¥']);
const THREE_DECIMAL_SYMBOLS = new Set(['KWD', 'BHD', 'JOD']);

/**
 * Format a numeric value with a currency symbol.
 *
 * @param amount  - The numeric amount
 * @param symbol  - Currency symbol (e.g. 'S$', 'RM', '฿')
 * @param decimals - Optional override for decimal places
 */
export function formatCurrency(
  amount: number,
  symbol: string = '$',
  decimals?: number,
): string {
  if (isNaN(amount) || !isFinite(amount)) return `${symbol}0.00`;

  let dp = decimals;
  if (dp === undefined) {
    if (NO_DECIMAL_SYMBOLS.has(symbol)) {
      dp = 0;
    } else if (THREE_DECIMAL_SYMBOLS.has(symbol)) {
      dp = 3;
    } else {
      dp = 2;
    }
  }

  const formatted = Math.abs(amount).toFixed(dp);
  const sign = amount < 0 ? '-' : '';
  return `${sign}${symbol}${formatted}`;
}

/**
 * Parse a currency string back to a number.
 * Strips the symbol and any thousands separators.
 */
export function parseCurrency(value: string): number {
  const cleaned = value.replace(/[^0-9.-]/g, '');
  return parseFloat(cleaned) || 0;
}

/**
 * Round to the nearest 5 cents (used for SGD cash rounding).
 */
export function roundToNearest5Cents(amount: number): number {
  return Math.round(amount * 20) / 20;
}

/**
 * Round to nearest integer (used for currencies without decimals like IDR, VND).
 */
export function roundToWhole(amount: number): number {
  return Math.round(amount);
}
